import React from "react";
import { format } from "date-fns";
import { Link } from "react-router-dom";
import { SignedIn, SignedOut, useUser } from "@clerk/clerk-react";

import { Container } from "../Container/Container";
import { Button } from "../Button/Button";
import { LoadingSpinner } from "../LoadingSpinner/LoadingSpinner";
import { EmptyStateBox } from "../EmptyStateBox/EmptyStateBox";
import { Paragraph, Span, Bold } from "../Typography/Typography";
import {
    recommendAlbum,
    fetchRecommendations,
    fetchAlbumListenedByUserIds,
    Album,
    Recommendation,
} from "../../api/albums";
import { getFollowers } from "../../api/follows";
import { ClerkUser, fetchClerkUsersByExternalIds } from "../../api/clerk";

import "./AlbumRecommendSidebar.scss";

type AlbumRecommendSidebarProps = {
    album: Album;
};

export function AlbumRecommendSidebar({ album }: AlbumRecommendSidebarProps) {
    const { user, isLoaded } = useUser();
    const [friends, setFriends] = React.useState<ClerkUser[]>([]);
    const [recommenders, setRecommenders] = React.useState<ClerkUser[]>([]);
    const [pendingByFriendId, setPendingByFriendId] = React.useState<
        Record<string, Recommendation>
    >({});
    const [recommendedToYouByFriendIds, setRecommendedToYouByFriendIds] = React.useState<
        Set<string>
    >(new Set());
    const [listenedFriendIds, setListenedFriendIds] = React.useState<Set<string>>(new Set());
    const [listenedOn, setListenedOn] = React.useState<string | null>(null);
    const [isLoading, setIsLoading] = React.useState(true);
    const [recommendingTo, setRecommendingTo] = React.useState<string | null>(null);
    const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

    const loadSidebarData = React.useCallback(async () => {
        if (!isLoaded || !user?.externalId) {
            setFriends([]);
            setRecommenders([]);
            setPendingByFriendId({});
            setRecommendedToYouByFriendIds(new Set());
            setListenedFriendIds(new Set());
            setListenedOn(null);
            setIsLoading(false);
            return;
        }

        setIsLoading(true);

        try {
            const [friendsData, sentRecommendations, receivedRecommendations, listenedUserIds] =
                await Promise.all([
                    getFollowers(user.externalId),
                    fetchRecommendations(user.externalId, { direction: "sent", status: "queue" }),
                    fetchRecommendations(user.externalId, { direction: "received" }),
                    fetchAlbumListenedByUserIds(album.id),
                ]);

            setFriends(friendsData);

            const pending: Record<string, Recommendation> = {};
            for (const rec of sentRecommendations) {
                pending[String(rec.to_user_id)] = rec;
            }
            setPendingByFriendId(pending);

            const receivedForAlbum = receivedRecommendations.filter(
                (rec) =>
                    Number(rec.album_id) === album.id &&
                    (rec.status === "queue" || rec.status === "done")
            );
            const listenedRec = receivedRecommendations.find(
                (rec) => Number(rec.album_id) === album.id && rec.status === "done"
            );
            setListenedOn(listenedRec?.updated_on ?? null);

            const recommenderIds = [
                ...new Set(receivedForAlbum.map((rec) => String(rec.from_user_id))),
            ];
            setRecommendedToYouByFriendIds(new Set(recommenderIds));

            const recommenderUsers = await fetchClerkUsersByExternalIds(recommenderIds);
            setRecommenders(recommenderUsers);

            setListenedFriendIds(new Set(listenedUserIds));
        } catch (error) {
            console.error("Error loading friends for recommend sidebar:", error);
            setFriends([]);
            setRecommenders([]);
            setPendingByFriendId({});
            setRecommendedToYouByFriendIds(new Set());
            setListenedFriendIds(new Set());
            setListenedOn(null);
        } finally {
            setIsLoading(false);
        }
    }, [album.id, isLoaded, user?.externalId]);

    React.useEffect(() => {
        loadSidebarData();
    }, [loadSidebarData]);

    const handleRecommend = async (friend: ClerkUser) => {
        if (!friend.externalId) {
            return;
        }

        setRecommendingTo(friend.externalId);
        setErrorMessage(null);

        try {
            const recommendation = await recommendAlbum({
                to_user_id: friend.externalId,
                album_id: album.id,
            });
            setPendingByFriendId((prev) => ({
                ...prev,
                [friend.externalId!]: recommendation,
            }));
        } catch (error: unknown) {
            console.error("Recommend failed:", error);
            setErrorMessage(
                axiosErrorMessage(error) ?? "Could not send recommendation. Please try again."
            );
        } finally {
            setRecommendingTo(null);
        }
    };

    return (
        <Container as="aside" className="album-recommend-sidebar" aria-label="Recommend to friends">
            <SignedOut>
                <h2 className="album-recommend-sidebar__title">Recommend to a friend</h2>
                <Paragraph variant="body-2">
                    <Link to="/login">Sign in</Link> to recommend this album.
                </Paragraph>
            </SignedOut>

            <SignedIn>
                {!isLoading && (listenedOn || recommenders.length > 0) && (
                    <div className="album-recommend-sidebar__meta">
                        {listenedOn && (
                            <section
                                className="album-recommend-sidebar__listened"
                                aria-label="Listened on"
                            >
                                <h2 className="album-recommend-sidebar__title">Listened on</h2>
                                <Paragraph variant="body-2" color="mid-contrast">
                                    {format(new Date(listenedOn), "MMM d, yyyy")}
                                </Paragraph>
                            </section>
                        )}

                        {recommenders.length > 0 && (
                            <section
                                className="album-recommend-sidebar__received"
                                aria-label="Recommended to you"
                            >
                                <h2 className="album-recommend-sidebar__title">
                                    Recommended to you by...
                                </h2>
                                <ul className="album-recommend-sidebar__recommenders">
                                    {recommenders.map((recommender) =>
                                        recommender.username ? (
                                            <li key={recommender.id}>
                                                <Link to={`/${recommender.username}`}>
                                                    <img
                                                        src={recommender.imageUrl}
                                                        alt=""
                                                        width={32}
                                                        height={32}
                                                    />
                                                    <Span variant="body-3">
                                                        <Bold>@{recommender.username}</Bold>
                                                    </Span>
                                                </Link>
                                            </li>
                                        ) : null
                                    )}
                                </ul>
                            </section>
                        )}
                    </div>
                )}

                <h2 className="album-recommend-sidebar__title">Recommend to a friend</h2>

                {isLoading && <LoadingSpinner />}

                {!isLoading && friends.length === 0 && (
                    <EmptyStateBox>
                        No friends yet.{" "}
                        <Link to={`/${user?.username}/friends`}>Add friends</Link> to start trading
                        albums.
                    </EmptyStateBox>
                )}

                {errorMessage && (
                    <Paragraph className="album-recommend-sidebar__error" variant="body-2">
                        {errorMessage}
                    </Paragraph>
                )}

                {!isLoading && friends.length > 0 && (
                    <ul className="album-recommend-sidebar__list">
                        {friends.map((friend) => {
                            const friendId = friend.externalId ?? "";
                            const pending = pendingByFriendId[friendId];
                            const hasPending = Boolean(pending);
                            const isSameAlbum =
                                hasPending && Number(pending.album_id) === album.id;
                            const hasListened = listenedFriendIds.has(friendId);
                            const recommendedThisToYou =
                                recommendedToYouByFriendIds.has(friendId);
                            const isBlocked =
                                hasPending || hasListened || recommendedThisToYou;
                            const isSending = recommendingTo === friendId;

                            let buttonToRender = null;
                            if (!isBlocked) {
                                buttonToRender = (
                                    <Button
                                        type="button"
                                        variant="solid-brand"
                                        size="small"
                                        disabled={isSending}
                                        onClick={() => handleRecommend(friend)}
                                    >
                                        {isSending ? "Sending…" : "Recommend"}
                                    </Button>
                                );
                            }

                            return (
                                <li key={friend.id} className="album-recommend-sidebar__item">
                                    <div className="album-recommend-sidebar__friend">
                                        <img
                                            src={friend.imageUrl}
                                            alt=""
                                            width={40}
                                            height={40}
                                        />
                                        <div className="album-recommend-sidebar__friend-meta">
                                            <Span variant="body-2">
                                                <Bold>@{friend.username}</Bold>
                                            </Span>
                                            {recommendedThisToYou && (
                                                <Span
                                                    className="album-recommend-sidebar__pending"
                                                    variant="body-3"
                                                    color="mid-contrast"
                                                >
                                                    Recommended this album to you
                                                </Span>
                                            )}
                                            {!recommendedThisToYou && hasListened && (
                                                <Span
                                                    className="album-recommend-sidebar__pending"
                                                    variant="body-3"
                                                    color="mid-contrast"
                                                >
                                                    Already listened to this album
                                                </Span>
                                            )}
                                            {!recommendedThisToYou &&
                                                !hasListened &&
                                                hasPending && (
                                                    <Span
                                                        className="album-recommend-sidebar__pending"
                                                        variant="body-3"
                                                        color="mid-contrast"
                                                    >
                                                        {isSameAlbum
                                                            ? "You already shared this album with them"
                                                            : `Has a pending album from you: ${pending.title} — ${pending.artist_name}`}
                                                    </Span>
                                                )}
                                        </div>
                                    </div>
                                    {buttonToRender}
                                </li>
                            );
                        })}
                    </ul>
                )}
            </SignedIn>
        </Container>
    );
}

function axiosErrorMessage(error: unknown) {
    if (
        typeof error === "object" &&
        error !== null &&
        "response" in error &&
        typeof (error as { response?: { data?: { error?: string } } }).response?.data?.error ===
            "string"
    ) {
        return (error as { response: { data: { error: string } } }).response.data.error;
    }

    return null;
}
